import { useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ChallengeFormModal from './ChallengeFormModal';
import { PRIMARY_CHALLENGE_GAME_TYPES } from './dailyTasksUtils';

function ChallengesPanel({
  challenges,
  challengeTypes,
  onCreate,
  onUpdate,
  onDelete,
  onReorder,
  saving,
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [createGameType, setCreateGameType] = useState('quiz');
  const [pendingDelete, setPendingDelete] = useState(null);

  const sorted = (challenges || [])
    .slice()
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  const move = async (index, dir) => {
    const next = sorted.slice();
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    const tmp = next[index];
    next[index] = next[target];
    next[target] = tmp;
    await onReorder(next.map((row, i) => ({ id: row.id, sort_order: i + 1 })));
  };

  const openCreate = (gameType) => {
    setEditing(null);
    setCreateGameType(gameType);
    setModalOpen(true);
  };

  return (
    <Box>
      <Alert severity="warning" sx={{ mb: 2 }}>
        <strong>Daily Page only</strong> (<code>config.surface=daily_page</code>). Challenges live in{' '}
        <code>daily_page_activities</code>. BE may create a hidden <code>category=daily_page</code>{' '}
        play row (not Arcade <code>GET /games</code>). Never writes Arcade{' '}
        <code>super_over_daily_challenges</code> — use <code>/admin/arcade/super-over-daily</code>{' '}
        for that.
      </Alert>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="subtitle1">
          Challenges <Chip size="small" label={sorted.length} sx={{ ml: 1 }} />
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => openCreate('quiz')}
          >
            Add Quiz
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => openCreate('fastest_finger')}
          >
            Add Fastest Finger
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => openCreate('super_over')}
          >
            Add Super Over
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => openCreate('snapshot')}
          >
            Add launcher
          </Button>
        </Box>
      </Box>

      {sorted.length === 0 ? (
        <Typography color="text.secondary">No Daily Page challenges for this date.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Order</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>FE</TableCell>
              <TableCell>Surface</TableCell>
              <TableCell>game_id</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((row, index) => (
              <TableRow key={row.id}>
                <TableCell>
                  <IconButton size="small" disabled={index === 0 || saving} onClick={() => move(index, -1)}>
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={index === sorted.length - 1 || saving}
                    onClick={() => move(index, 1)}
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                  {row.sort_order ?? index + 1}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.game_type || row.config?.game_type || 'challenge'}
                  />
                </TableCell>
                <TableCell>{row.title || '—'}</TableCell>
                <TableCell>
                  {row.fe_prize_per_participation ??
                    row.config?.fe_prize_per_participation ??
                    row.fan_energy_reward ??
                    0}
                </TableCell>
                <TableCell>
                  <Chip size="small" color="info" label={row.config?.surface || 'daily_page'} />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" component="code">
                    {row.game_id || row.config?.game_id || '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditing(row);
                      setCreateGameType(
                        row.game_type || row.config?.game_type || 'quiz'
                      );
                      setModalOpen(true);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => setPendingDelete(row)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <ChallengeFormModal
        open={modalOpen}
        onClose={() => {
          if (!saving) {
            setModalOpen(false);
            setEditing(null);
          }
        }}
        onSubmit={async (payload) => {
          if (editing?.id) await onUpdate(editing.id, payload);
          else await onCreate(payload);
          setModalOpen(false);
          setEditing(null);
        }}
        challenge={editing}
        initialGameType={createGameType}
        lockGameType={!editing && PRIMARY_CHALLENGE_GAME_TYPES.includes(createGameType)}
        challengeTypes={challengeTypes}
        saving={saving}
      />

      <Dialog open={!!pendingDelete} onClose={() => !saving && setPendingDelete(null)}>
        <DialogTitle>Delete Daily Page challenge?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deactivate “{pendingDelete?.title}” (
            {pendingDelete?.game_type || pendingDelete?.config?.game_type}) on Daily Page only?
            Does not touch Arcade Super Over Daily.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)} disabled={saving}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            onClick={async () => {
              await onDelete(pendingDelete.id);
              setPendingDelete(null);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ChallengesPanel;
